import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

const CREAM = '#F5F1E9';
const FOREST = '#1D4D3A';
const FOREST_MUTED = '#2D4A3E';
const INK = '#1A1A1A';
const MUTED = '#757575';
const HAIRLINE = 'rgba(26, 26, 26, 0.12)';
const SERIF = "'Playfair Display', 'Crimson Pro', Georgia, serif";

const SRED_PATTERN = /sr\s*&?\s*ed|sred/i;
const RD_AI_LABEL = 'R&D + AI';

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return null;
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function isSredItem(item) {
  return SRED_PATTERN.test(item?.upgradeName || '');
}

function displayUpgradeName(name) {
  if (!name) return 'Funding Upgrade';
  if (SRED_PATTERN.test(name)) return RD_AI_LABEL;
  return name;
}

function SuccessCard({ item, index }) {
  const displayDate = formatDate(item.date);
  const isCashCollected = item.status === 'Cash Collected';
  const title = displayUpgradeName(item.upgradeName);
  const isRdAi = isSredItem(item);

  return (
    <motion.article
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.03, 0.45), ease: 'easeOut' }}
      className="group flex flex-col border-t py-8 transition-colors duration-200"
      style={{ borderColor: HAIRLINE }}
    >
      <div className="mb-5 flex items-center justify-between gap-3">
        <span
          className="text-[11px] font-medium uppercase tracking-[0.16em]"
          style={{ color: isRdAi ? FOREST_MUTED : MUTED }}
        >
          {item.industry}
        </span>
        <span
          className="text-[11px] font-medium uppercase tracking-[0.14em]"
          style={{ color: isCashCollected ? FOREST_MUTED : MUTED }}
        >
          {isCashCollected ? 'Collected' : 'Awarded'}
        </span>
      </div>

      <p
        className="text-3xl font-medium tracking-tight sm:text-4xl"
        style={{ color: INK, fontFamily: SERIF }}
      >
        {formatCurrency(item.amount)}
      </p>

      <h3
        className="mt-4 text-lg leading-snug line-clamp-2"
        style={{ color: INK, fontFamily: SERIF }}
      >
        {title}
      </h3>

      <p className="mt-auto pt-6 text-sm" style={{ color: MUTED }}>
        {displayDate || 'Date not available'}
      </p>
    </motion.article>
  );
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }}>
      <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 lg:px-12">
        <Skeleton
          height={20}
          width={140}
          baseColor="#E8E2D8"
          highlightColor="#F5F1E9"
          className="mb-6"
        />
        <Skeleton
          height={56}
          width={420}
          baseColor="#E8E2D8"
          highlightColor="#F5F1E9"
          className="mb-4"
        />
        <Skeleton
          height={24}
          width={480}
          baseColor="#E8E2D8"
          highlightColor="#F5F1E9"
          className="mb-16"
        />
        <div className="mb-12 grid gap-8 border-t sm:grid-cols-3" style={{ borderColor: HAIRLINE }}>
          {[1, 2, 3].map((id) => (
            <Skeleton key={id} height={100} baseColor="#E8E2D8" highlightColor="#F5F1E9" />
          ))}
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((id) => (
            <Skeleton key={id} height={160} baseColor="#E8E2D8" highlightColor="#F5F1E9" />
          ))}
        </div>
      </div>
    </div>
  );
}

export default function ClientSuccessWallPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('featured');

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      try {
        const response = await fetch('/api/client-success-wall');
        if (!response.ok) {
          throw new Error('Failed to load success stories');
        }
        const json = await response.json();
        if (!cancelled) {
          setData(json);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Something went wrong');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const sortedItems = useMemo(() => {
    if (!data?.items) return [];
    const items = [...data.items];

    const byDate = (a, b) => {
      if (a.date && b.date) return new Date(b.date) - new Date(a.date);
      if (a.date) return -1;
      if (b.date) return 1;
      return b.amount - a.amount;
    };

    const byAmount = (a, b) => b.amount - a.amount;

    if (sortBy === 'amount') {
      return items.sort(byAmount);
    }

    if (sortBy === 'date') {
      return items.sort(byDate);
    }

    // Featured: R&D + AI first, then the rest by most recent
    return items.sort((a, b) => {
      const aRd = isSredItem(a) ? 0 : 1;
      const bRd = isSredItem(b) ? 0 : 1;
      if (aRd !== bRd) return aRd - bRd;
      return byDate(a, b);
    });
  }, [data?.items, sortBy]);

  if (loading) {
    return <LoadingSkeleton />;
  }

  if (error) {
    return (
      <div
        className="flex min-h-screen items-center justify-center px-6"
        style={{ backgroundColor: CREAM }}
      >
        <div className="max-w-md text-center">
          <p
            className="text-[11px] font-medium uppercase tracking-[0.18em]"
            style={{ color: FOREST_MUTED }}
          >
            Something went wrong
          </p>
          <p className="mt-4 text-2xl" style={{ color: INK, fontFamily: SERIF }}>
            Unable to load the wall
          </p>
          <p className="mt-3 text-sm" style={{ color: MUTED }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  const stats = data?.stats || { totalAmount: 0, totalCount: 0, industriesCount: 0 };

  return (
    <div className="min-h-screen" style={{ backgroundColor: CREAM }}>
      {/* Hero — forest green editorial panel */}
      <section style={{ backgroundColor: FOREST }}>
        <div className="mx-auto max-w-6xl px-6 py-16 sm:px-10 sm:py-20 lg:px-12 lg:py-24">
          <div className="flex flex-col gap-12 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <p
                className="text-[11px] font-medium uppercase tracking-[0.2em]"
                style={{ color: 'rgba(245, 241, 233, 0.7)' }}
              >
                Client results
              </p>
              <h1
                className="mt-6 text-4xl font-medium leading-[1.15] tracking-tight sm:text-5xl lg:text-[3.5rem]"
                style={{ color: CREAM, fontFamily: SERIF }}
              >
                Wall of Client Success
              </h1>
              <p
                className="mt-6 max-w-xl text-base leading-7 sm:text-lg sm:leading-8"
                style={{ color: 'rgba(245, 241, 233, 0.72)' }}
              >
                Every result represents funding secured for a Canadian company through Upgraded —
                grants, tax credits, and savings that turned into real dollars.
              </p>
            </div>
            <a
              href="https://getupgraded.ca"
              className="inline-flex shrink-0 items-center justify-center border px-6 py-3 text-sm font-medium transition-colors duration-150 hover:bg-[rgba(245,241,233,0.08)]"
              style={{
                color: CREAM,
                borderColor: 'rgba(245, 241, 233, 0.35)',
              }}
            >
              Get funded with Upgraded
            </a>
          </div>

          <div
            className="mt-14 grid gap-2 border-t pt-2 sm:grid-cols-3 sm:gap-10"
            style={{ borderColor: 'rgba(245, 241, 233, 0.2)' }}
          >
            <div className="py-6">
              <p
                className="text-[11px] font-medium uppercase tracking-[0.18em]"
                style={{ color: 'rgba(245, 241, 233, 0.55)' }}
              >
                Total funding secured
              </p>
              <p
                className="mt-3 text-3xl font-medium tracking-tight sm:text-4xl"
                style={{ color: CREAM, fontFamily: SERIF }}
              >
                {formatCurrency(stats.totalAmount)}
              </p>
              <p className="mt-2 text-sm" style={{ color: 'rgba(245, 241, 233, 0.5)' }}>
                Awarded &amp; cash collected
              </p>
            </div>
            <div className="py-6">
              <p
                className="text-[11px] font-medium uppercase tracking-[0.18em]"
                style={{ color: 'rgba(245, 241, 233, 0.55)' }}
              >
                Successful upgrades
              </p>
              <p
                className="mt-3 text-3xl font-medium tracking-tight sm:text-4xl"
                style={{ color: CREAM, fontFamily: SERIF }}
              >
                {stats.totalCount.toLocaleString()}
              </p>
              <p className="mt-2 text-sm" style={{ color: 'rgba(245, 241, 233, 0.5)' }}>
                Across our client base
              </p>
            </div>
            <div className="py-6">
              <p
                className="text-[11px] font-medium uppercase tracking-[0.18em]"
                style={{ color: 'rgba(245, 241, 233, 0.55)' }}
              >
                Industries represented
              </p>
              <p
                className="mt-3 text-3xl font-medium tracking-tight sm:text-4xl"
                style={{ color: CREAM, fontFamily: SERIF }}
              >
                {stats.industriesCount.toLocaleString()}
              </p>
              <p className="mt-2 text-sm" style={{ color: 'rgba(245, 241, 233, 0.5)' }}>
                From SaaS to manufacturing
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stories */}
      <div className="mx-auto max-w-6xl px-6 py-14 sm:px-10 sm:py-16 lg:px-12">
        <div className="mb-2 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p
              className="text-[11px] font-medium uppercase tracking-[0.18em]"
              style={{ color: FOREST_MUTED }}
            >
              Success stories
            </p>
            <h2
              className="mt-3 text-2xl font-medium tracking-tight sm:text-3xl"
              style={{ color: INK, fontFamily: SERIF }}
            >
              {sortedItems.length}{' '}
              {sortedItems.length === 1 ? 'result' : 'results'}
            </h2>
          </div>
          <div className="flex gap-1 border-b" style={{ borderColor: HAIRLINE }}>
            {[
              { id: 'featured', label: 'Featured' },
              { id: 'date', label: 'Most recent' },
              { id: 'amount', label: 'Highest amount' },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setSortBy(tab.id)}
                className="px-3 pb-3 text-sm transition-colors duration-150"
                style={{
                  color: sortBy === tab.id ? INK : MUTED,
                  borderBottom:
                    sortBy === tab.id ? `1.5px solid ${INK}` : '1.5px solid transparent',
                  marginBottom: '-1px',
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {sortedItems.length === 0 ? (
          <div className="border-t py-20 text-center" style={{ borderColor: HAIRLINE }}>
            <p
              className="text-[11px] font-medium uppercase tracking-[0.18em]"
              style={{ color: MUTED }}
            >
              Coming soon
            </p>
            <p className="mt-4 text-xl" style={{ color: INK, fontFamily: SERIF }}>
              No success stories yet
            </p>
            <p className="mt-2 text-sm" style={{ color: MUTED }}>
              Awarded and cash-collected opportunities will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="mt-4 grid gap-x-12 sm:grid-cols-2 lg:grid-cols-3">
            {sortedItems.map((item, index) => (
              <SuccessCard key={item.id} item={item} index={index} />
            ))}
          </div>
        )}

        <footer
          className="mt-16 flex flex-col gap-3 border-t pt-8 sm:flex-row sm:items-center sm:justify-between"
          style={{ borderColor: HAIRLINE }}
        >
          <p
            className="text-[11px] font-medium uppercase tracking-[0.18em]"
            style={{ color: MUTED }}
          >
            Upgraded
          </p>
          <p className="text-sm sm:text-right" style={{ color: MUTED }}>
            Company names are kept private. Amounts reflect awarded funding secured through{' '}
            <a
              href="https://getupgraded.ca"
              className="transition-colors duration-150 hover:opacity-70"
              style={{ color: FOREST_MUTED }}
            >
              Upgraded
            </a>
            .
          </p>
        </footer>
      </div>
    </div>
  );
}
